import React, { useEffect } from "react";
import cn from "classnames";
import Tooltip from "@material-ui/core/Tooltip";
import { Editor, TLParentId, TLShapeId } from "@tldraw/tldraw";
import { getClosestContainer } from "../../utils";
import { AlignToSideIcon } from "./Primitives/icons/AlignToSideIcon";
import { AlignToCenterIcon } from "./Primitives/icons/AlignToCenterIcon";
import AlignThreeVLineIcon from "./Primitives/icons/AlignThreeVLineIcon";
import AlignTopBottomIcon from "./Primitives/icons/AlignTopBottomIcon";
import useTooltipStyles from "./style";
import { AlignLeftIcon } from "components/icons/components/AlignLeftIcon";
import { AlignCenterIcon } from "components/icons/components/AlignCenterIcon";
import { AlignRightIcon } from "components/icons/components/AlignRightIcon";
import { VerticalDitributeIcon } from "components/icons/components/VerticalDitributeIcon";
import HorizontalDistributeIcon from "components/icons/components/HorizontalDistributeIcon";
import { AlignTopIcon } from "components/icons/components/AlignTopIcon";
import { TextAlignCenterIcon } from "@radix-ui/react-icons";
import { AlignBottomIcon } from "components/icons/components/AlignBottomIcon";

interface AlignActionsProps {
  editor: Editor;
  selectedShape: any;
  selectedShapeIds: any[];
  selectedShapes: any[];
}

type AlignOperations = "bottom" | "center-horizontal" | "center-vertical" | "left" | "right" | "top";
type StretchOperation = "horizontal" | "vertical";
type DistOperation = "vertical" | "horizontal";
type AllOperations = AlignOperations | StretchOperation | DistOperation;

const isAlignOperation = (operation: AllOperations): operation is AlignOperations => {
  return ["left", "right", "top", "bottom", "center-horizontal", "center-vertical"].includes(operation);
};
const isStretchOperation = (operation: AllOperations, label: string): operation is StretchOperation => {
  return label.includes("Stretch") && ["vertical", "horizontal"].includes(operation);
};
const isDistOperation = (operation: AllOperations, label: string): operation is DistOperation => {
  return label.includes("Distribute") && ["vertical", "horizontal"].includes(operation);
};

const AlignActions = ({ editor, selectedShape, selectedShapeIds, selectedShapes }: AlignActionsProps) => {
  const frontSaveZoneId = "shape:frontBorderDashLine" as TLParentId;
  const backSaveZoneId = "shape:backBorderDashLine" as TLParentId;

  const classes = useTooltipStyles();

  const handleAlign = (operation: AllOperations, label: string) => {
    if (selectedShapes.length === 0 && !selectedShape) {
      console.warn("No selected shapes found");
      return;
    }

    // Attempt to find the closest container using the first shape in selectedShapes or selectedShape as a fallback
    const referenceShape = selectedShape || selectedShapes[0];
    if (!referenceShape) {
      console.warn("No valid reference shape found");
      return;
    }

    const closestContainer = getClosestContainer(frontSaveZoneId, backSaveZoneId, editor, referenceShape);

    if (!closestContainer) {
      console.warn("No container found for the reference shape");
      return;
    }

    let shapeIdsToAlign: TLShapeId[] = [];

    if (selectedShapes.length > 1) {
      // For multiple shapes, align them directly without grouping
      shapeIdsToAlign = selectedShapes.filter((shape) => shape && shape.id).map((shape) => shape.id);
    } else {
      // For single shape, align with the closest container
      shapeIdsToAlign = [referenceShape.id, closestContainer.id];
    }

    // Perform alignment operations
    if (isAlignOperation(operation)) {
      editor.alignShapes(shapeIdsToAlign, operation);
    }

    if (isStretchOperation(operation, label)) {
      editor.stretchShapes(shapeIdsToAlign, operation);
    }

    if (isDistOperation(operation, label)) {
      editor.distributeShapes(shapeIdsToAlign, operation);
    }
  };

  const horizontalAlignments: {
    label: string;
    operation: AllOperations;
    rotate: number;
    icon: JSX.Element;
  }[] = [
    { label: "Align Left", operation: "left", rotate: 0, icon: <AlignLeftIcon /> },
    { label: "Align Center Horizontally", operation: "center-horizontal", rotate: 0, icon: <AlignCenterIcon /> },
    { label: "Align Right", operation: "right", rotate: 0, icon: <AlignRightIcon /> },
  ];

  const verticalAlignments: {
    label: string;
    operation: AllOperations;
    rotate: number;
    icon: JSX.Element;
  }[] = [
    { label: "Align Top", operation: "top", rotate: 0, icon: <AlignTopIcon /> },
    { label: "Align Center Vertically", operation: "center-vertical", rotate: -90, icon: <AlignCenterIcon /> },
    { label: "Align Bottom", operation: "bottom", rotate: 0, icon: <AlignBottomIcon /> },
  ];

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.altKey && e.key === "a") {
        handleAlign("left", "Align Left");
        e.stopPropagation();
      }
      if (e.altKey && e.key === "d") {
        handleAlign("right", "Align Right");
        e.stopPropagation();
      }
      if (e.altKey && e.key === "w") {
        handleAlign("top", "Align Top");
        e.stopPropagation();
      }
      if (e.altKey && e.key === "s") {
        handleAlign("bottom", "Align Bottom");
        e.stopPropagation();
      }
      if (e.altKey && e.key === "h") {
        handleAlign("center-horizontal", "Align Center Horizontal");
        e.stopPropagation();
      }
      if (e.altKey && e.key === "v") {
        handleAlign("center-vertical", "Align Center Vertical");
        e.stopPropagation();
      }
      if ((e.ctrlKey || e.metaKey) && e.altKey && e.code === "KeyV") {
        handleAlign("vertical", "Distribute Vertical");
        window.dispatchEvent(new CustomEvent("flyer:distribute-mode", { detail: { mode: "vertical" } }));
        e.stopPropagation();
      }
      if ((e.ctrlKey || e.metaKey) && e.altKey && e.code === "KeyH") {
        handleAlign("horizontal", "Distribute Horizontal");
        window.dispatchEvent(new CustomEvent("flyer:distribute-mode", { detail: { mode: "horizontal" } }));
        e.stopPropagation();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [selectedShape]);

  return (
    <div className={cn("px-4")}>
      <p className="text-[14px] font-semibold text-[#2E3860] mt-[8px] mb-[8px] leading-[24px]">Alignment</p>
      <div className="flex items-center gap-2">
        {/* Horizontal alignment group */}
        <div
          className="flex items-center"
          style={{
            border: "1px solid #E4E4EA",
            borderRadius: "4px",
          }}
        >
          {horizontalAlignments.map(({ label, operation, rotate, icon }, index) => (
            <Tooltip title={label} arrow key={`horizontal-${index}`} classes={{ tooltip: classes.tooltip }}>
              <div
                onClick={() => handleAlign(operation as any, label)}
                style={{
                  padding: "8px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRight: index < horizontalAlignments.length - 1 ? "1px solid #E4E4EA" : "none",
                }}
                className="flex max-w-[38px] max-h-[38px] hover:bg-[#E2E2FC] transition-colors"
              >
                <div
                  style={{
                    transform: `rotate(${rotate}deg)`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {icon}
                </div>
              </div>
            </Tooltip>
          ))}
        </div>

        {/* Vertical alignment group */}
        <div
          className="flex items-center"
          style={{
            border: "1px solid #E4E4EA",
            borderRadius: "4px",
          }}
        >
          {verticalAlignments.map(({ label, operation, rotate, icon }, index) => (
            <Tooltip title={label} arrow key={`vertical-${index}`} classes={{ tooltip: classes.tooltip }}>
              <div
                onClick={() => handleAlign(operation as any, label)}
                style={{
                  padding: "8px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRight: index < verticalAlignments.length - 1 ? "1px solid #E4E4EA" : "none",
                }}
                className="flex max-w-[38px] max-h-[38px] hover:bg-[#E2E2FC] transition-colors"
              >
                <div
                  style={{
                    transform: `rotate(${rotate}deg)`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {icon}
                </div>
              </div>
            </Tooltip>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AlignActions;
